import React, { useContext, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    Tab,
    Tabs,
    TextField,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../App";
import { SystemMail, SystemMailLocalization } from "../../model/SystemMail";
import { Language } from "../../model/Language";
import {
    createSystemMail,
    updateSystemMail,
    deleteSystemMail,
} from "../../api/systemMailClient";
import classes from "../../styling/components/Editor.module.css";
import { useConfirmationDialog } from "../../hooks/useConfirmationDialog";
import FroalaEditorComponent from "react-froala-wysiwyg";
import "froala-editor/css/froala_style.min.css";
import "froala-editor/css/froala_editor.pkgd.min.css";

function emptyLocalization(lang: Language): SystemMailLocalization {
    return {
        language: lang,
        name: "",
        senderAddress: "",
        replyToAddress: "",
        subject: "",
        body: "",
    };
}

function availableLanguagesFromTemplate(template: SystemMail | undefined): Language[] {
    if (!template?.localizations?.length) return [];
    return template.localizations.map((l) => l.language);
}

function localizationsByLangFromTemplate(template: SystemMail | undefined): Partial<Record<Language, SystemMailLocalization>> {
    if (!template?.localizations?.length) return {};
    const record: Partial<Record<Language, SystemMailLocalization>> = {};
    for (const loc of template.localizations) {
        record[loc.language] = { ...loc };
    }
    return record;
}

interface Props {
    method: "POST" | "PUT";
    template?: SystemMail;
}

const getApiBaseUrl = () => {
    switch (process.env.NODE_ENV) {
        case "production":
            return "/api";
        case "development":
            return "http://localhost:5000/api";
        default:
            return "/api";
    }
};

const SystemMailTemplateEditor: React.FC<Props> = (props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { customerId } = useParams();
    const { authHeader } = useContext(UserContext);
    const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();

    const [availableLanguages, setAvailableLanguages] = useState<Language[]>(() =>
        availableLanguagesFromTemplate(props.template)
    );
    const [localizationsByLang, setLocalizationsByLang] = useState<Partial<Record<Language, SystemMailLocalization>>>(() =>
        localizationsByLangFromTemplate(props.template)
    );
    const [activeLanguage, setActiveLanguage] = useState<Language | null>(() => {
        const langs = availableLanguagesFromTemplate(props.template);
        return langs[0] ?? null;
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const SENDER_OPTIONS = ["no-reply@mail.look2x.de", "awareness@mail.look2x.de"] as const;

    const updateLocalization = (lang: Language, field: keyof SystemMailLocalization, value: string | Language) => {
        setLocalizationsByLang((prev) => {
            const loc = prev[lang] ?? emptyLocalization(lang);
            const next = { ...loc, [field]: value };
            if (field === "senderAddress" && typeof value === "string") {
                next.replyToAddress = value;
            }
            return { ...prev, [lang]: next };
        });
    };

    const addLanguage = (language: Language) => {
        if (availableLanguages.includes(language)) return;
        setAvailableLanguages((prev) => [...prev, language]);
        setLocalizationsByLang((prev) => ({ ...prev, [language]: emptyLocalization(language) }));
        setActiveLanguage(language);
    };

    const removeLanguage = (language: Language) => {
        const nextLangs = availableLanguages.filter((l) => l !== language);
        setAvailableLanguages(nextLangs);
        setLocalizationsByLang((prev) => {
            const next = { ...prev };
            delete next[language];
            return next;
        });
        if (activeLanguage === language) {
            setActiveLanguage(nextLangs[0] ?? null);
        }
    };

    const buildPayload = (): SystemMail => ({
        ...(props.template?.templateId && { templateId: props.template.templateId }),
        localizations: availableLanguages.map((lang) => {
            const loc = localizationsByLang[lang] ?? emptyLocalization(lang);
            const { language, name, senderAddress, subject, body } = loc;
            const replyToAddress = loc.senderAddress ?? loc.replyToAddress ?? "";
            return { language, name, senderAddress, replyToAddress, subject, body };
        }),
    });

    const BODY_MAX_LENGTH = 15000;

    const validate = (): string | null => {
        if (availableLanguages.length === 0) return t("systemMailTemplate.editor.atLeastOneLocalization");
        const hasContent = availableLanguages.some((lang) => {
            const loc = localizationsByLang[lang];
            return loc && loc.name.trim() && loc.senderAddress.trim() && loc.subject.trim();
        });
        if (!hasContent) return t("systemMailTemplate.editor.atLeastOneLocalization");
        const tooLong = availableLanguages.some((lang) => {
            const loc = localizationsByLang[lang];
            return loc && (loc.body?.length ?? 0) > BODY_MAX_LENGTH;
        });
        if (tooLong) return t("systemMailTemplate.editor.bodyTooLong");
        return null;
    };

    const submitHandler = async () => {
        const err = validate();
        if (err) {
            setSaveError(err);
            return;
        }
        setSaveError(null);
        setIsSubmitting(true);
        try {
            const payload = buildPayload();
            if (props.method === "POST") {
                const created = await createSystemMail(payload, customerId!);
                navigate(`/customers/${customerId}/system-mails/${created.templateId}`);
            } else {
                await updateSystemMail(
                    payload,
                    props.template!.templateId!,
                    customerId!
                );
                navigate(`/customers/${customerId}/system-mails`);
            }
        } catch {
            setSaveError(t("systemMailTemplate.table.saveError"));
        } finally {
            setIsSubmitting(false);
        }
    };

    const cancelHandler = () => {
        navigate(`/customers/${customerId}/system-mails`);
    };

    const deleteHandler = () => {
        openConfirmationDialog(
            t("systemMailTemplate.table.deleteTitle"),
            t("systemMailTemplate.table.deleteContent"),
            async () => {
                if (!props.template?.templateId || !customerId) return;
                try {
                    await deleteSystemMail(props.template.templateId, customerId);
                    navigate(`/customers/${customerId}/system-mails`);
                } catch {
                    setSaveError(t("systemMailTemplate.table.deleteError"));
                }
            }
        );
    };

    return (
        <>
            {confirmationDialog}
            <Card>
                <CardContent>
                    <Box className={classes.form}>
                        {availableLanguages.length === 0 ? (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center", py: 4 }}>
                                <Box sx={{ textAlign: "center", mb: 2 }}>
                                    <h3>{t("templateEditor.addTemplateContent")}</h3>
                                    <p>{t("templateEditor.chooseLanguageToStart")}</p>
                                </Box>
                                <Box sx={{ display: "flex", gap: 2 }}>
                                    <Button
                                        variant="contained"
                                        onClick={() => addLanguage(Language.DE)}
                                        sx={{ minWidth: 120 }}
                                    >
                                        {t("templateEditor.addGerman")}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={() => addLanguage(Language.EN)}
                                        sx={{ minWidth: 120 }}
                                    >
                                        {t("templateEditor.addEnglish")}
                                    </Button>
                                </Box>
                            </Box>
                        ) : (
                            <>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                                    <Tabs value={activeLanguage ?? false} onChange={(_, v) => setActiveLanguage(v as Language)}>
                                        {availableLanguages.map((lang) => (
                                            <Tab key={lang} label={lang.toUpperCase()} value={lang} />
                                        ))}
                                    </Tabs>
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                        {!availableLanguages.includes(Language.DE) && (
                                            <Button size="small" variant="outlined" onClick={() => addLanguage(Language.DE)}>
                                                {t("templateEditor.addGermanShort")}
                                            </Button>
                                        )}
                                        {!availableLanguages.includes(Language.EN) && (
                                            <Button size="small" variant="outlined" onClick={() => addLanguage(Language.EN)}>
                                                {t("templateEditor.addEnglishShort")}
                                            </Button>
                                        )}
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            onClick={() => activeLanguage && removeLanguage(activeLanguage)}
                                        >
                                            {t("templateEditor.removeLanguage", { language: activeLanguage?.toUpperCase() })}
                                        </Button>
                                    </Box>
                                </Box>

                                {availableLanguages.map((lang) => (
                                    <div
                                        key={lang}
                                        role="tabpanel"
                                        hidden={activeLanguage !== lang}
                                        id={`tabpanel-${lang}`}
                                    >
                                        {activeLanguage === lang && (() => {
                                            const loc = localizationsByLang[lang] ?? emptyLocalization(lang);
                                            return (
                                                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                                    <TextField
                                                        fullWidth
                                                        label={t("templateEditor.nameLabel")}
                                                        value={loc.name}
                                                        onChange={(e) => updateLocalization(lang, "name", e.target.value)}
                                                        helperText={t("templateEditor.nameHelper")}
                                                    />
                                                    <Autocomplete
                                                        freeSolo
                                                        disableClearable
                                                        options={SENDER_OPTIONS.slice()}
                                                        value={loc.senderAddress ?? ""}
                                                        onInputChange={(_, value) =>
                                                            updateLocalization(lang, "senderAddress", value ?? "")
                                                        }
                                                        onChange={(_, value) =>
                                                            updateLocalization(lang, "senderAddress", typeof value === "string" ? value : value ?? "")
                                                        }
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={t("templateEditor.senderAddressLabel")}
                                                                helperText={t("templateEditor.senderAddressHelper")}
                                                            />
                                                        )}
                                                    />
                                                    <TextField
                                                        fullWidth
                                                        label={t("templateEditor.replyToAddressLabel")}
                                                        value={loc.replyToAddress ?? loc.senderAddress ?? ""}
                                                        InputProps={{ readOnly: true }}
                                                        helperText={t("templateEditor.replyToAddressHelper")}
                                                    />
                                                    <TextField
                                                        fullWidth
                                                        label={t("templateEditor.subjectLabel")}
                                                        value={loc.subject}
                                                        onChange={(e) =>
                                                            updateLocalization(lang, "subject", e.target.value)
                                                        }
                                                        helperText={t("templateEditor.subjectHelper")}
                                                    />
                                                    <FroalaEditorComponent
                                                        tag="textarea"
                                                        model={loc.body ?? ""}
                                                        onModelChange={(value: string) => updateLocalization(lang, "body", value)}
                                                        config={{
                                                            key: "dKA5cD3A2E2B2G1B2B1B6a1lxfpZGCTRSAPJWTLPLZHTQQb1JGZxE2F2G2I1B10B2C2E6F1E1==",
                                                            htmlRemoveTags: ["script"],
                                                            toolbarButtons: [
                                                                ["bold", "italic", "underline", "textColor", "clearFormatting"],
                                                                ["formatOL", "formatUL", "paragraphFormat"],
                                                                ["insertLink", "insertImage", "insertTable"],
                                                                ["html", "fullscreen", "undo", "redo"],
                                                            ],
                                                            imageOutputSize: true,
                                                            requestHeaders: {
                                                                authorization: authHeader ?? "",
                                                            },
                                                            imageUploadParam: "file",
                                                            imageUploadURL: getApiBaseUrl() + "/images",
                                                            imageManagerLoadURL: getApiBaseUrl() + "/images",
                                                            imageManagerDeleteURL: getApiBaseUrl() + "/images",
                                                            imageManagerDeleteMethod: "DELETE",
                                                            imageUploadMethod: "POST",
                                                            imageMaxSize: 5 * 1024 * 1024,
                                                            imageAllowedTypes: ["jpeg", "jpg", "png"],
                                                        }}
                                                    />
                                                </Box>
                                            );
                                        })()}
                                    </div>
                                ))}
                            </>
                        )}

                        {saveError && (
                            <Alert
                                severity="error"
                                onClose={() => setSaveError(null)}
                                sx={{ mt: 2, mb: 2 }}
                            >
                                {saveError}
                            </Alert>
                        )}

                        <Divider sx={{ my: 2 }} />

                        <div>
                            {props.method === "PUT" && (
                                <Button
                                    type="button"
                                    className={classes.delete}
                                    variant="contained"
                                    onClick={deleteHandler}
                                    disabled={isSubmitting}
                                >
                                    {t("templateEditor.deactivate")}
                                </Button>
                            )}
                            <Button
                                type="button"
                                variant="outlined"
                                onClick={cancelHandler}
                                disabled={isSubmitting}
                            >
                                {t("systemMailCampaign.editor.cancel")}
                            </Button>
                            <Button
                                type="button"
                                variant="contained"
                                onClick={submitHandler}
                                disabled={isSubmitting || availableLanguages.length === 0}
                            >
                                {t("templateEditor.save")}
                            </Button>
                        </div>
                    </Box>
                </CardContent>
            </Card>
        </>
    );
};

export default SystemMailTemplateEditor;
